import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { SkuActionsService } from '@shared/services';
import type { TSkuAction } from '@shared/models';
import { ViewBasePage } from '@shared/pages';

@Component({
    'selector': 'app-skuactions',
    'templateUrl': './skuactions.page.html',
    'styleUrls': ['./skuactions.page.scss'],
    'standalone': true,
    'imports': [IonicModule, CommonModule, FormsModule, RouterLink]
})
export class SkuActionsPage extends ViewBasePage implements OnInit, OnDestroy {
    public submitted = false;
    public allSkuActions: TSkuAction[] = [];

    // private wooCommerceService: WooCommerceService,
    constructor(private skuActionsService: SkuActionsService, injector: Injector) {
        super(injector);
    }

    public override async ngOnInit() {
        try {
            await this.uxHelperService.showSpinner();

            // Get only the visible products.
            // const allProducts = await this.wooCommerceService.getProducts();
            const allProducts = [];
            const products = allProducts.filter((product) => {
                const visible = (product.catalog_visibility === 'visible');
                const hasSku = !this.utilsService.isBlank(product.sku);
                // const hasSku2 = !product.sku;   // Compare hasSku & hasSku2.  Remove isBlank
                return visible && hasSku;
            });
            await this.createSkuActionForEachProduct(products);
            await this.syncDescriptions(products);

            const allSkuActions = await this.skuActionsService.getAll();
            this.allSkuActions = this.skuActionsService.sort(allSkuActions);

            await this.uxHelperService.hideSpinner();
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            await this.uxHelperService.showAlert('Error', errorMessage);
            await this.uxHelperService.hideSpinner();
        }
    }

    public override ngOnDestroy() {
        super.ngOnDestroy();
    }

    private async createSkuActionForEachProduct(products: any) {
        const skuActions = await this.skuActionsService.getAll();

        const newSkus = this.findNewWoocommerceProducts(skuActions, products);
        if (newSkus.length > 0) {
            await this.skuActionsService.addAll(newSkus);
        }
    }

    private findNewWoocommerceProducts(allSkuActions: TSkuAction[], products: any[]) {
        const newSkus: any = [];

        // const newSkus = products.map(product => {
        products.forEach(product => {
            const sku = allSkuActions.find(skuAction =>
                // console.log(JSON.stringify(product));
                (skuAction.sku === product.sku)
            );
            if (!sku) {
                newSkus.push({ 'sku': product.sku, 'description': product.name });
            }
        });
        return newSkus;
    }

    private async syncDescriptions(products: any): Promise<any[]> {
        const skuActions = await this.skuActionsService.getAll();
        const promises: Promise<TSkuAction>[] = [];

        if (skuActions.length === 0 || products.length === 0) {
            return promises;
        }

        products.map(product => {
            const skuAction = skuActions.find(skuAction => (skuAction.sku === product.sku));
            if (skuAction.description !== product.name) {
                skuAction.description = product.name;
                const promise = this.skuActionsService.update(skuAction);
                promises.push(promise);
            }
        });
        return await Promise.all(promises);
    }

    public onSkuActionClick(skuAction: TSkuAction) {
        this.router.navigateByUrl('/skuAction/' + skuAction.uid);
    }

    public onAddSkuActionClick(): void {
        this.router.navigateByUrl('/skuAction');
    }

    public buildActionSummary(skuAction: TSkuAction): string {
        const actions: string[] = [];

        if (skuAction.increaseExpirationDays > 0 ||
            skuAction.increaseExpirationMonths > 0 ||
            skuAction.increaseExpirationYears > 0) {
            const amount = `${skuAction.increaseExpirationYears} Years, 
                            ${skuAction.increaseExpirationMonths} Months,
                            ${skuAction.increaseExpirationDays} Days`;
            actions.push(`Bump Expiration By ${amount}`);
        }

        if (skuAction.increasePremiumSupportMonths > 0 || skuAction.increasePremiumSupportYears > 0) {
            const amount = `${skuAction.increasePremiumSupportYears} Years, 
                            ${skuAction.increasePremiumSupportMonths} Months`;
            actions.push(`Bump Premium Support By ${amount}`);
        }

        if (skuAction.increaseWebHostingMonths > 0 || skuAction.increaseWebHostingYears > 0) {
            const amount = `${skuAction.increaseWebHostingYears} Years, 
                            ${skuAction.increaseWebHostingMonths} Months`;
            actions.push(`Bump Web Hosting By ${amount}`);
        }

        if (skuAction.updateActivationInfo) {
            actions.push(`Update Activation Info`);
        }

        if (skuAction.updateCredits) {
            actions.push(`Update Credits`);
        }

        // if (skuAction.increasePremiumSupportMonths > 0) {
        //     actions.push(`Bump Premium Support By ${skuAction.increasePremiumSupportMonths} months`);
        // }

        // if (skuAction.increasePremiumSupportYears > 0) {
        //     actions.push(`Bump Premium Support By ${skuAction.increasePremiumSupportYears} years`);
        // }

        // if (skuAction.increaseWebHostingMonths > 0) {
        //     actions.push(`Bump Web Hosting By ${skuAction.increaseWebHostingMonths} months`);
        // }

        // if (skuAction.increaseWebHostingYears > 0) {
        //     actions.push(`Bump Web Hosting By ${skuAction.increaseWebHostingYears} years`);
        // }

        if (skuAction.sendCompleteOrderEmail) {
            actions.push('Send Complete Order Email To Support');
        }

        if (skuAction.updatePackageDetails) {
            actions.push('Update Package Details');
        }
        let description = 'No Actions Defined';
        if (actions.length > 0) {
            description = actions.join('; ');
        }
        return description;
    }
}
